import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import api from "../Utils/api";
import Select from "react-select";
import Spinner from "../Components/Spinner";
import Map, { Marker, Source, Layer } from "@vis.gl/react-mapbox";
import * as turf from "@turf/turf";
import * as XLSX from "xlsx";
import QRImage from "../Assets/image/qrcode.png";
import NFCImage from "../Assets/image/nfc.png";

const MAPBOX_TOKEN = "pk.eyJ1IjoiYWxlcnRsaW5lIiwiYSI6ImNtODMweDM2ZzAwdGQycXI0enVwZndtOHgifQ.yJ8KJp8yHrr3midLMoDbHg";

export default function ScanEvents() {
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store all users for filtering
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]); // Store all locations for filtering
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Check if user is logged in via location credentials
  const isLocationLogin = user?.loginType === "location" && user?.locationID;

  // Filters
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Dropdown states
  const [isUsersDropdownOpen, setIsUsersDropdownOpen] = useState(false);
  const [isEventTypesDropdownOpen, setIsEventTypesDropdownOpen] = useState(false);
  const [isCompaniesDropdownOpen, setIsCompaniesDropdownOpen] = useState(false);
  const [isLocationsDropdownOpen, setIsLocationsDropdownOpen] = useState(false);
  const [usersSearchTerm, setUsersSearchTerm] = useState("");
  const [companiesSearchTerm, setCompaniesSearchTerm] = useState("");
  const [locationsSearchTerm, setLocationsSearchTerm] = useState("");
  const usersDropdownRef = useRef(null);
  const eventTypesDropdownRef = useRef(null);
  const companiesDropdownRef = useRef(null);
  const locationsDropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Map state
  const [viewState, setViewState] = useState({
    latitude: 53.276749,
    longitude: -6.21918,
    zoom: 10,
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isProgrammaticUpdate = useRef(false);

  useEffect(() => {
    if (user) {
      // Set default date range to today
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      setStartDate(startOfDay.toISOString().slice(0, 16));
      setEndDate(endOfDay.toISOString().slice(0, 16));

      // For location-based login, auto-select the location
      if (isLocationLogin && user.locationID) {
        setSelectedLocations([user.locationID]);
      } else {
        // Only fetch users/locations/companies for regular users
        getUsers();
        getLocations();
        if (user.deputyAccessLevel === "system_administrator") {
          getCompanies();
        }
      }
    }
  }, [user, isLocationLogin]);

  useEffect(() => {
    if (startDate && endDate) {
      searchEvents();
    }
  }, [startDate, endDate, selectedUsers, selectedEventTypes, selectedCompanies, selectedLocations]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside any dropdown button or portal dropdown
      const isInsideUsers = usersDropdownRef.current?.contains(event.target) || 
                           document.querySelector('[data-dropdown="users"]')?.contains(event.target);
      const isInsideEventTypes = eventTypesDropdownRef.current?.contains(event.target) ||
                                document.querySelector('[data-dropdown="eventTypes"]')?.contains(event.target);
      const isInsideCompanies = companiesDropdownRef.current?.contains(event.target) ||
                               document.querySelector('[data-dropdown="companies"]')?.contains(event.target);
      const isInsideLocations = locationsDropdownRef.current?.contains(event.target) ||
                               document.querySelector('[data-dropdown="locations"]')?.contains(event.target);

      // Only close if click is outside ALL dropdowns
      if (!isInsideUsers && !isInsideEventTypes && !isInsideCompanies && !isInsideLocations) {
        setIsUsersDropdownOpen(false);
        setIsEventTypesDropdownOpen(false);
        setIsCompaniesDropdownOpen(false);
        setIsLocationsDropdownOpen(false);
        setActiveDropdown(null);
      }
    };

    if (isUsersDropdownOpen || isEventTypesDropdownOpen || isCompaniesDropdownOpen || isLocationsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUsersDropdownOpen, isEventTypesDropdownOpen, isCompaniesDropdownOpen, isLocationsDropdownOpen]);

  const getUsers = async () => {
    try {
      const response = await api.get("/user");
      if (response.data.users) {
        // Only show employees, not system_administrator or company_manager
        const employeeUsers = response.data.users.filter((u) => 
          !u.archive && u.deputyAccessLevel === "employee"
        );
        setAllUsers(employeeUsers); // Store all users
        // Apply company filter if companies are selected
        filterUsersByCompanies(employeeUsers);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch users");
    }
  };

  // Filter users based on selected companies
  const filterUsersByCompanies = (usersToFilter = allUsers) => {
    if (selectedCompanies.length === 0) {
      // No companies selected, show all users
      setUsers(usersToFilter);
    } else {
      // Filter users by selected companies
      const filtered = usersToFilter.filter((user) => {
        const userCompanyID = user.companyID?._id || user.companyID;
        return userCompanyID && selectedCompanies.includes(userCompanyID.toString());
      });
      setUsers(filtered);
      // Also update selectedUsers to remove users that are no longer in the filtered list
      setSelectedUsers((prev) => 
        prev.filter((userId) => filtered.some((u) => u._id === userId))
      );
    }
  };

  const getCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        // Only show active (non-archived) companies
        setCompanies((response.data.companies || []).filter(company => !company.archive));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getLocations = async () => {
    try {
      const response = await api.get("/location");
      if (response.data.locations) {
        // Only show active (non-archived) locations
        const activeLocations = (response.data.locations || []).filter(loc => !loc.archive);
        setAllLocations(activeLocations); // Store all locations
        // Apply company filter if companies are selected
        filterLocationsByCompanies(activeLocations);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch locations");
    }
  };

  // Filter locations based on selected companies
  const filterLocationsByCompanies = (locationsToFilter = allLocations) => {
    if (selectedCompanies.length === 0) {
      // No companies selected, show all locations
      setLocations(locationsToFilter);
    } else {
      // Filter locations by selected companies
      const filtered = locationsToFilter.filter((location) => {
        const locationCompanyID = location.companyID?._id || location.companyID;
        return locationCompanyID && selectedCompanies.includes(locationCompanyID.toString());
      });
      setLocations(filtered);
      // Also update selectedLocations to remove locations that are no longer in the filtered list
      setSelectedLocations((prev) => 
        prev.filter((locationId) => filtered.some((l) => l._id === locationId))
      );
    }
  };

  // Update users and locations when companies selection changes
  useEffect(() => {
    if (allUsers.length > 0) {
      filterUsersByCompanies(allUsers);
    }
    if (allLocations.length > 0) {
      filterLocationsByCompanies(allLocations);
    }
  }, [selectedCompanies]);

  const searchEvents = async () => {
    try {
      setIsLoading(true);
      const searchData = {
        startTime: startDate ? new Date(startDate).toISOString() : null,
        endTime: endDate ? new Date(endDate).toISOString() : null,
        user_ids: isLocationLogin ? null : (selectedUsers.length > 0 ? selectedUsers : null),
        event_types: isLocationLogin ? null : (selectedEventTypes.length > 0 ? selectedEventTypes.map((t) => t.value) : null),
        company_ids: isLocationLogin ? null : (selectedCompanies.length > 0 ? selectedCompanies : null),
        location_ids: isLocationLogin ? [user.locationID] : (selectedLocations.length > 0 ? selectedLocations : null),
      };

      const response = await api.post("/scanevent/search", searchData);
      setIsLoading(false);

      if (response.data.success) {
        setEvents(response.data.events || []);
        setFilteredEvents(response.data.events || []);
        setCurrentPage(1);
      } else {
        toast.error("Failed to search events");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to search events");
      setIsLoading(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    if (event.latitude && event.longitude) {
      isProgrammaticUpdate.current = true;
      setViewState({
        latitude: parseFloat(event.latitude),
        longitude: parseFloat(event.longitude),
        zoom: 15,
      });
      // Reset flag after a short delay to allow map to update
      setTimeout(() => {
        isProgrammaticUpdate.current = false;
      }, 100);
    }
  };

  const handleZoomIn = () => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 1, 22),
    }));
  };

  const handleZoomOut = () => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 1, 0),
    }));
  };

  const handleZoomToScannedLocation = () => {
    if (selectedEvent && selectedEvent.latitude && selectedEvent.longitude) {
      isProgrammaticUpdate.current = true;
      setViewState({
        latitude: parseFloat(selectedEvent.latitude),
        longitude: parseFloat(selectedEvent.longitude),
        zoom: 15,
      });
      // Reset flag after a short delay to allow map to update
      setTimeout(() => {
        isProgrammaticUpdate.current = false;
      }, 100);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    // Always display scan times in Ireland timezone (DST-safe)
    return date.toLocaleString("en-IE", {
      timeZone: "Europe/Dublin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false, // 24-hour format
    });
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case "QRScan":
        return "text-green-800";
      case "NFCSCAN":
        return "text-orange-800";
      case "GPSScan":
        return "text-blue-800";
      default:
        return "text-gray-800";
    }
  };

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredEvents.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredEvents.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (value) => {
    const rows = parseInt(value);
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const getUserFullName = (user) => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || user.preferredName || user.email || "Unknown";
  };

  const getFilteredUsers = () => {
    if (!usersSearchTerm) return users;
    return users.filter((user) => {
      const fullName = getUserFullName(user);
      return fullName.toLowerCase().includes(usersSearchTerm.toLowerCase());
    });
  };

  const toggleUserSelection = (userID) => {
    setSelectedUsers((prev) =>
      prev.includes(userID)
        ? prev.filter((id) => id !== userID)
        : [...prev, userID]
    );
  };

  const toggleAllUsers = () => {
    const filteredUsers = getFilteredUsers();
    const allFilteredSelected = filteredUsers.every((u) => selectedUsers.includes(u._id));
    if (allFilteredSelected) {
      // Deselect all filtered users
      setSelectedUsers((prev) => prev.filter((id) => !filteredUsers.some((u) => u._id === id)));
    } else {
      // Select all filtered users
      const newSelected = [...selectedUsers];
      filteredUsers.forEach((u) => {
        if (!newSelected.includes(u._id)) {
          newSelected.push(u._id);
        }
      });
      setSelectedUsers(newSelected);
    }
  };

  const getSelectedUsersNames = () => {
    if (selectedUsers.length === 0) return "All Users";
    if (selectedUsers.length === users.length) return "All Users";
    const selected = users.filter((u) => selectedUsers.includes(u._id));
    if (selected.length === 1) return getUserFullName(selected[0]);
    return `${selected.length} users selected`;
  };

  const toggleEventTypeSelection = (eventType) => {
    setSelectedEventTypes((prev) => {
      const exists = prev.some((t) => t.value === eventType);
      if (exists) {
        return prev.filter((t) => t.value !== eventType);
      } else {
        let label = "NFC Scan";
        if (eventType === "QRScan") {
          label = "QR Scan";
        } else if (eventType === "GPSScan") {
          label = "GPS Scan";
        }
        return [...prev, { label, value: eventType }];
      }
    });
  };

  const toggleAllEventTypes = () => {
    const allTypes = [
      { label: "QR Scan", value: "QRScan" },
      { label: "NFC Scan", value: "NFCSCAN" },
      { label: "GPS Scan", value: "GPSScan" },
    ];
    const allSelected = allTypes.every((type) =>
      selectedEventTypes.some((t) => t.value === type.value)
    );
    if (allSelected) {
      setSelectedEventTypes([]);
    } else {
      setSelectedEventTypes(allTypes);
    }
  };

  const getSelectedEventTypesNames = () => {
    if (selectedEventTypes.length === 0) return "All Types";
    if (selectedEventTypes.length === 3) return "All Types";
    return selectedEventTypes.map((t) => t.label).join(", ");
  };

  // Company filter functions
  const getFilteredCompanies = () => {
    if (!companiesSearchTerm) return companies;
    return companies.filter((company) =>
      company.name.toLowerCase().includes(companiesSearchTerm.toLowerCase())
    );
  };

  const toggleCompanySelection = (companyID) => {
    setSelectedCompanies((prev) =>
      prev.includes(companyID)
        ? prev.filter((id) => id !== companyID)
        : [...prev, companyID]
    );
  };

  const toggleAllCompanies = () => {
    const filteredCompanies = getFilteredCompanies();
    const allFilteredSelected = filteredCompanies.every((c) => selectedCompanies.includes(c._id));
    if (allFilteredSelected) {
      setSelectedCompanies((prev) => prev.filter((id) => !filteredCompanies.some((c) => c._id === id)));
    } else {
      const newSelected = [...selectedCompanies];
      filteredCompanies.forEach((c) => {
        if (!newSelected.includes(c._id)) {
          newSelected.push(c._id);
        }
      });
      setSelectedCompanies(newSelected);
    }
  };

  const getSelectedCompaniesNames = () => {
    if (selectedCompanies.length === 0) return "All Companies";
    if (selectedCompanies.length === companies.length) return "All Companies";
    const selected = companies.filter((c) => selectedCompanies.includes(c._id));
    if (selected.length === 1) return selected[0].name;
    return `${selected.length} companies selected`;
  };

  // Location filter functions
  const getFilteredLocations = () => {
    if (!locationsSearchTerm) return locations;
    return locations.filter((location) =>
      location.name.toLowerCase().includes(locationsSearchTerm.toLowerCase())
    );
  };

  const toggleLocationSelection = (locationID) => {
    setSelectedLocations((prev) =>
      prev.includes(locationID)
        ? prev.filter((id) => id !== locationID)
        : [...prev, locationID]
    );
  };

  const toggleAllLocations = () => {
    const filteredLocations = getFilteredLocations();
    const allFilteredSelected = filteredLocations.every((l) => selectedLocations.includes(l._id));
    if (allFilteredSelected) {
      setSelectedLocations((prev) => prev.filter((id) => !filteredLocations.some((l) => l._id === id)));
    } else {
      const newSelected = [...selectedLocations];
      filteredLocations.forEach((l) => {
        if (!newSelected.includes(l._id)) {
          newSelected.push(l._id);
        }
      });
      setSelectedLocations(newSelected);
    }
  };

  const getSelectedLocationsNames = () => {
    if (selectedLocations.length === 0) return "All Locations";
    if (selectedLocations.length === locations.length) return "All Locations";
    const selected = locations.filter((l) => selectedLocations.includes(l._id));
    if (selected.length === 1) return selected[0].name;
    return `${selected.length} locations selected`;
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (filteredEvents.length === 0) {
      toast.warn("No events to export");
      return;
    }

    const headers = [
      "Date",
      "Type",
      "User",
      "Checkpoint",
      "Location",
      "Range",
      "Coordinates (Latitude, Longitude)",
    ];

    const data = filteredEvents.map((event) => {
      const userName = event.userID
        ? `${event.userID.firstName || ""} ${event.userID.lastName || ""}`.trim() ||
          event.userID.preferredName ||
          event.userID.email ||
          "-"
        : "-";
      
      const location = event.latitude && event.longitude
        ? `${parseFloat(event.latitude).toFixed(4)}, ${parseFloat(event.longitude).toFixed(4)}`
        : "-";

      return [
        formatDate(event.event_date),
        event.type,
        userName,
        event.checkpointID?.name || "-",
        event.checkpointID?.locationID?.name || "-",
        event.scan_range === "in" ? "IN" : "OUT",
        location,
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scan Events");

    // Generate filename with date range
    const startDateStr = startDate ? new Date(startDate).toISOString().split("T")[0] : "all";
    const endDateStr = endDate ? new Date(endDate).toISOString().split("T")[0] : "all";
    const filename = `scan-events-${startDateStr}-${endDateStr}.xlsx`;
    
    XLSX.writeFile(wb, filename);
    toast.success("Excel file exported successfully");
  };

  // Create markers for map
  const markers = filteredEvents
    .filter((e) => e.latitude && e.longitude)
    .map((e) => ({
      id: e._id,
      lat: parseFloat(e.latitude),
      lng: parseFloat(e.longitude),
      type: e.type,
      scan_range: e.scan_range,
    }));

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'px-4 sm:px-6 mt-20 mx-4 sm:mx-10 pb-20'}`}>
      {isLoading && <Spinner />}

      {/* Header */}
      {!isFullscreen && (
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">Scan Events</h1>
        <button
          onClick={handleExportExcel}
          disabled={filteredEvents.length === 0}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            filteredEvents.length === 0
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gray-500 text-white hover:bg-gray-600"
          }`}
        >
          <i className="fa fa-file-excel mr-2"></i>
          Export Excel
        </button>
      </div>
      )}

      <div className={`flex ${isFullscreen ? 'h-screen w-screen gap-0' : 'gap-2 sm:gap-4'} ${isFullscreen ? 'h-screen' : 'h-[calc(100vh-12rem)] max-h-[calc(100vh-12rem)]'}`} style={!isFullscreen ? { minHeight: '500px', maxHeight: 'calc(100vh - 12rem)' } : {}}>
        {/* Left Panel - Map */}
        <div className={`${isFullscreen ? 'w-full h-full' : 'w-2/5 sm:w-1/3 min-w-[250px]'} flex flex-col flex-shrink-0`}>
          {/* Map */}
          <div className={`bg-white ${isFullscreen ? 'h-full rounded-none' : 'rounded-lg shadow-md'} overflow-hidden flex flex-col h-full`}>
            <div className={`h-[42px] flex justify-between items-center bg-gray-800 ${isFullscreen ? 'rounded-none' : 'rounded-t-md'} px-4`}>
              <div className="flex items-center">
                <i className="fa fa-map-marker text-white mr-2"></i>
                <span className="text-white font-semibold">Map</span>
              </div>
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-gray-300"
              >
                <i className={isFullscreen ? "fa fa-compress" : "fa fa-expand"}></i>
              </button>
            </div>
            <div className={`${isFullscreen ? 'h-[calc(100vh-42px)]' : 'flex-1'} relative`}>
              {/* Zoom Controls */}
              <div className="absolute top-4 left-4 z-10 flex flex-col bg-white rounded-lg shadow-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={handleZoomIn}
                  className="px-3 py-2 hover:bg-gray-100 border-b border-gray-300 flex items-center justify-center transition-colors"
                  title="Zoom In"
                >
                  <i className="fa fa-plus text-gray-700"></i>
                </button>
                <button
                  onClick={handleZoomOut}
                  className="px-3 py-2 hover:bg-gray-100 flex items-center justify-center transition-colors"
                  title="Zoom Out"
                >
                  <i className="fa fa-minus text-gray-700"></i>
                </button>
              </div>

              <Map
                {...viewState}
                onMove={(evt) => {
                  // Only update viewState if it's not a programmatic update
                  if (!isProgrammaticUpdate.current) {
                    setViewState(evt.viewState);
                  }
                }}
                style={{ width: "100%", height: "100%" }}
                mapStyle="mapbox://styles/mapbox/streets-v12"
                mapboxAccessToken={MAPBOX_TOKEN}
              >
                {markers.map((marker) => (
                  <Marker
                    key={marker.id}
                    longitude={marker.lng}
                    latitude={marker.lat}
                    onClick={() => {
                      const event = events.find((e) => e._id === marker.id);
                      if (event) handleEventClick(event);
                    }}
                  >
                    <div
                      className={`w-6 h-6 rounded-full border-2 border-white cursor-pointer ${
                        marker.scan_range === "in" ? "bg-green-500" : "bg-red-500"
                      } ${selectedEvent?._id === marker.id ? "ring-4 ring-blue-400" : ""}`}
                      title={`${marker.type} - ${marker.scan_range}`}
                    />
                  </Marker>
                ))}
              </Map>
            </div>
          </div>
        </div>

        {/* Right Panel - Filters and Table */}
        {!isFullscreen && (
        <div className="w-3/5 sm:w-2/3 flex flex-col min-w-0 overflow-hidden flex-1">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex-shrink-0 overflow-visible" style={{ maxHeight: 'none' }}>
            <div className="grid grid-cols-2 gap-4">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>

              {/* Company Filter - Only for system administrators and not location-based login */}
              {!isLocationLogin && user && user.deputyAccessLevel === "system_administrator" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <div className="relative" ref={companiesDropdownRef} style={{ zIndex: 1000 }}>
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.left + window.scrollX,
                          width: rect.width
                        });
                        setActiveDropdown('companies');
                        setIsCompaniesDropdownOpen(!isCompaniesDropdownOpen);
                        setCompaniesSearchTerm("");
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {getSelectedCompaniesNames()}
                      </span>
                      <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Company Dropdown - Rendered outside container */}
              {isCompaniesDropdownOpen && activeDropdown === 'companies' && createPortal(
                <div 
                  data-dropdown="companies"
                  className="fixed bg-white border border-gray-300 rounded-md shadow-xl z-[9999] max-h-80 overflow-hidden flex flex-col"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search"
                        value={companiesSearchTerm}
                        onChange={(e) => setCompaniesSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                        autoFocus
                      />
                      <i className="fa fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                    </div>
                  </div>

                  <div className="overflow-y-auto max-h-64">
                    <div
                      onClick={toggleAllCompanies}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                          {getFilteredCompanies().length > 0 &&
                          getFilteredCompanies().every((c) => selectedCompanies.includes(c._id)) ? (
                            <i className="fa fa-check text-xs text-gray-600"></i>
                          ) : (
                            ""
                          )}
                        </div>
                        <span className="text-sm text-gray-700 font-medium">
                          {getFilteredCompanies().length > 0 &&
                          getFilteredCompanies().every((c) => selectedCompanies.includes(c._id))
                            ? "Deselect All"
                            : "Select All"}
                        </span>
                      </div>
                    </div>

                    {getFilteredCompanies().map((company) => (
                      <div
                        key={company._id}
                        onClick={() => toggleCompanySelection(company._id)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                            {selectedCompanies.includes(company._id) && (
                              <i className="fa fa-check text-xs text-gray-600"></i>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{company.name}</span>
                        </div>
                      </div>
                    ))}

                    {getFilteredCompanies().length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        No companies found
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}

              {/* Old Company Dropdown - Remove this */}
              {false && user && user.deputyAccessLevel === "system_administrator" && (
                <div>
                  <div className="relative" ref={companiesDropdownRef} style={{ zIndex: 1000 }}>
                    <button
                      onClick={() => {
                        setIsCompaniesDropdownOpen(!isCompaniesDropdownOpen);
                        setCompaniesSearchTerm("");
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {getSelectedCompaniesNames()}
                      </span>
                      <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
                    </button>

                    {isCompaniesDropdownOpen && (
                      <div className="absolute mt-1 w-full bg-white border border-gray-300 rounded-md shadow-xl z-[1001] max-h-80 overflow-hidden flex flex-col" style={{ position: 'absolute', top: '100%', left: 0, right: 0 }}>
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search"
                              value={companiesSearchTerm}
                              onChange={(e) => setCompaniesSearchTerm(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                              autoFocus
                            />
                            <i className="fa fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                          </div>
                        </div>

                        <div className="overflow-y-auto max-h-64">
                          <div
                            onClick={toggleAllCompanies}
                            className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                                {getFilteredCompanies().length > 0 &&
                                getFilteredCompanies().every((c) => selectedCompanies.includes(c._id)) ? (
                                  <i className="fa fa-check text-xs text-gray-600"></i>
                                ) : (
                                  ""
                                )}
                              </div>
                              <span className="text-sm text-gray-700 font-medium">
                                {getFilteredCompanies().length > 0 &&
                                getFilteredCompanies().every((c) => selectedCompanies.includes(c._id))
                                  ? "Deselect All"
                                  : "Select All"}
                              </span>
                            </div>
                          </div>

                          {getFilteredCompanies().map((company) => (
                            <div
                              key={company._id}
                              onClick={() => toggleCompanySelection(company._id)}
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                                  {selectedCompanies.includes(company._id) && (
                                    <i className="fa fa-check text-xs text-gray-600"></i>
                                  )}
                                </div>
                                <span className="text-sm text-gray-700">{company.name}</span>
                              </div>
                            </div>
                          ))}

                          {getFilteredCompanies().length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                              No companies found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location Filter - Hidden for location-based login */}
              {!isLocationLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <div className="relative" ref={locationsDropdownRef} style={{ zIndex: 1000 }}>
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.left + window.scrollX,
                          width: rect.width
                        });
                        setActiveDropdown('locations');
                        setIsLocationsDropdownOpen(!isLocationsDropdownOpen);
                        setLocationsSearchTerm("");
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {getSelectedLocationsNames()}
                      </span>
                      <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Location Dropdown - Rendered outside container */}
              {isLocationsDropdownOpen && activeDropdown === 'locations' && createPortal(
                <div 
                  data-dropdown="locations"
                  className="fixed bg-white border border-gray-300 rounded-md shadow-xl z-[9999] max-h-80 overflow-hidden flex flex-col"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search"
                        value={locationsSearchTerm}
                        onChange={(e) => setLocationsSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                        autoFocus
                      />
                      <i className="fa fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                    </div>
                  </div>

                  <div className="overflow-y-auto max-h-64">
                    <div
                      onClick={toggleAllLocations}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                          {getFilteredLocations().length > 0 &&
                          getFilteredLocations().every((l) => selectedLocations.includes(l._id)) ? (
                            <i className="fa fa-check text-xs text-gray-600"></i>
                          ) : (
                            ""
                          )}
                        </div>
                        <span className="text-sm text-gray-700 font-medium">
                          {getFilteredLocations().length > 0 &&
                          getFilteredLocations().every((l) => selectedLocations.includes(l._id))
                            ? "Deselect All"
                            : "Select All"}
                        </span>
                      </div>
                    </div>

                    {getFilteredLocations().map((location) => (
                      <div
                        key={location._id}
                        onClick={() => toggleLocationSelection(location._id)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                            {selectedLocations.includes(location._id) && (
                              <i className="fa fa-check text-xs text-gray-600"></i>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{location.name}</span>
                        </div>
                      </div>
                    ))}

                    {getFilteredLocations().length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        No locations found
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}

              {/* Users Filter - Hidden for location-based login */}
              {!isLocationLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Users
                  </label>
                  <div className="relative" ref={usersDropdownRef} style={{ zIndex: 1000 }}>
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.left + window.scrollX,
                          width: rect.width
                        });
                        setActiveDropdown('users');
                        setIsUsersDropdownOpen(!isUsersDropdownOpen);
                        setUsersSearchTerm("");
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {getSelectedUsersNames()}
                      </span>
                      <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Users Dropdown - Rendered outside container */}
              {isUsersDropdownOpen && activeDropdown === 'users' && createPortal(
                <div 
                  data-dropdown="users"
                  className="fixed bg-white border border-gray-300 rounded-md shadow-xl z-[9999] max-h-80 overflow-hidden flex flex-col"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search"
                        value={usersSearchTerm}
                        onChange={(e) => setUsersSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                        autoFocus
                      />
                      <i className="fa fa-search absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                    </div>
                  </div>

                  <div className="overflow-y-auto max-h-64">
                    <div
                      onClick={toggleAllUsers}
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                          {getFilteredUsers().length > 0 &&
                          getFilteredUsers().every((u) => selectedUsers.includes(u._id)) ? (
                            <i className="fa fa-check text-xs text-gray-600"></i>
                          ) : (
                            ""
                          )}
                        </div>
                        <span className="text-sm text-gray-700 font-medium">
                          {getFilteredUsers().length > 0 &&
                          getFilteredUsers().every((u) => selectedUsers.includes(u._id))
                            ? "Deselect All"
                            : "Select All"}
                        </span>
                      </div>
                    </div>

                    {getFilteredUsers().map((user) => (
                      <div
                        key={user._id}
                        onClick={() => toggleUserSelection(user._id)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                            {selectedUsers.includes(user._id) && (
                              <i className="fa fa-check text-xs text-gray-600"></i>
                            )}
                          </div>
                          <span className="text-sm text-gray-700">{getUserFullName(user)}</span>
                        </div>
                      </div>
                    ))}

                    {getFilteredUsers().length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        No users found
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}

              {/* Event Types Filter - Hidden for location-based login */}
              {!isLocationLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Types
                  </label>
                  <div className="relative" ref={eventTypesDropdownRef} style={{ zIndex: 1000 }}>
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownPosition({
                          top: rect.bottom + window.scrollY + 4,
                          left: rect.left + window.scrollX,
                          width: rect.width
                        });
                        setActiveDropdown('eventTypes');
                        setIsEventTypesDropdownOpen(!isEventTypesDropdownOpen);
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {getSelectedEventTypesNames()}
                      </span>
                      <i className="fa fa-chevron-down text-gray-400 text-xs ml-2"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Event Types Dropdown - Rendered outside container */}
              {isEventTypesDropdownOpen && activeDropdown === 'eventTypes' && createPortal(
                <div 
                  data-dropdown="eventTypes"
                  className="fixed bg-white border border-gray-300 rounded-md shadow-xl z-[9999]"
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    onClick={toggleAllEventTypes}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                        {selectedEventTypes.length === 3 ? (
                          <i className="fa fa-check text-xs text-gray-600"></i>
                        ) : (
                          ""
                        )}
                      </div>
                      <span className="text-sm text-gray-700 font-medium">
                        {selectedEventTypes.length === 3 ? "Deselect All" : "Select All"}
                      </span>
                    </div>
                  </div>

                  <div
                    onClick={() => toggleEventTypeSelection("QRScan")}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                        {selectedEventTypes.some((t) => t.value === "QRScan") && (
                          <i className="fa fa-check text-xs text-gray-600"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">QR Scan</span>
                    </div>
                  </div>

                  <div
                    onClick={() => toggleEventTypeSelection("NFCSCAN")}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                        {selectedEventTypes.some((t) => t.value === "NFCSCAN") && (
                          <i className="fa fa-check text-xs text-gray-600"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">NFC Scan</span>
                    </div>
                  </div>

                  <div
                    onClick={() => toggleEventTypeSelection("GPSScan")}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border border-gray-300 rounded flex items-center justify-center">
                        {selectedEventTypes.some((t) => t.value === "GPSScan") && (
                          <i className="fa fa-check text-xs text-gray-600"></i>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">GPS Scan</span>
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-md flex-1 overflow-hidden flex flex-col min-h-0" style={{ minHeight: '300px' }}>
            <div className="p-4 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-700">Events</h2>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: '200px' }}>
              <table className="w-full border-collapse">
                <thead className="bg-gray-200 sticky top-0">
                  <tr>
                    <th className="p-3 text-left font-bold text-gray-700">Date</th>
                    <th className="p-3 text-center font-bold text-gray-700">Type</th>
                    <th className="p-3 text-left font-bold text-gray-700">User</th>
                    <th className="p-3 text-left font-bold text-gray-700">Checkpoint</th>
                    <th className="p-3 text-left font-bold text-gray-700">Location</th>
                    <th className="p-3 text-center font-bold text-gray-700">Range</th>
                    <th className="p-3 text-left font-bold text-gray-700">Lat/Long</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-gray-500">
                        No events found
                      </td>
                    </tr>
                  ) : (
                    currentRows.map((event) => (
                      <tr
                        key={event._id}
                        className={`border-b border-gray-200 hover:bg-gray-50 cursor-pointer ${
                          selectedEvent?._id === event._id ? "bg-blue-50" : ""
                        } ${
                          (event.type === "QRScan" || event.type === "NFCSCAN" || event.type === "GPSScan") &&
                          event.scan_range === "out"
                            ? "text-red-600"
                            : ""
                        }`}
                        onClick={() => handleEventClick(event)}
                      >
                        <td className="p-3 text-gray-700">{formatDate(event.event_date)}</td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {event.type === "GPSScan" ? (
                              <i className="fa fa-map-marker-alt text-blue-600 text-xl" title="GPS"></i>
                            ) : (
                              <img
                                src={event.type === "QRScan" ? QRImage : NFCImage}
                                alt={event.type}
                                className="w-6 h-6"
                              />
                            )}
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${getEventTypeColor(
                                event.type
                              )}`}
                            >
                              {event.type}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-gray-700">
                          {event.userID
                            ? `${event.userID.firstName || ""} ${event.userID.lastName || ""}`.trim() ||
                              event.userID.preferredName ||
                              event.userID.email
                            : "-"}
                        </td>
                        <td className="p-3 text-gray-700">
                          {event.checkpointID?.name || "-"}
                        </td>
                        <td className="p-3 text-gray-700">
                          {event.checkpointID?.locationID?.name || "-"}
                        </td>
                        <td className="p-3 text-center">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              event.scan_range === "in"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {event.scan_range === "in" ? "IN" : "OUT"}
                          </span>
                        </td>
                        <td className="p-3 text-gray-700 text-sm">
                          {event.latitude && event.longitude
                            ? `${parseFloat(event.latitude).toFixed(4)}, ${parseFloat(event.longitude).toFixed(4)}`
                            : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="p-4 border-t flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Total: {filteredEvents.length} items
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(e.target.value)}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value={50}>50/page</option>
                  <option value={100}>100/page</option>
                  <option value={200}>200/page</option>
                  <option value={1000}>1000/page</option>
                </select>
                <div className="flex gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    &lt;
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 border border-gray-300 rounded text-sm ${
                          currentPage === pageNum
                            ? "bg-blue-500 text-white"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

